import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { EmailSessionModelWithEmailThread } from '@/api/models/emailSession';
import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { Id } from '@/api/utils/sql';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_GetAllSent_Email = {
  locationId?: Id;
  email?: string;
  filterBy?: 'sent' | 'archived';
};

export type ApiResponse_GetAllSent_Email = Array<EmailSessionModelWithEmailThread>;

const METHOD = 'GET';
const ENDPOINT = 'email/sent';
export const QUERY_KEY = (params: ApiRequest_GetAllSent_Email) =>
  getQueryKey(METHOD, ENDPOINT, params);

export const useGetAllSentEmails = (params: ApiRequest_GetAllSent_Email, enabled = true) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!params.locationId && !!session?.access_token,
    queryKey: QUERY_KEY(params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<ApiResponse_GetAllSent_Email, ApiRequest_GetAllSent_Email>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {}),
          params
        );

        if (!params.filterBy) return data;

        return data.filter(email => {
          if (params.filterBy === 'archived') return !!email.session.archived;
          if (params.filterBy === 'sent') return !email.session.archived;

          return true;
        });
      } catch (err) {
        enqueueSnackbar('Failed to fetch sent emails.', { variant: 'error' });

        // eslint-disable-next-line no-console
        console.error(err);

        throw err;
      }
    },
  });
};
